import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowRight, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { FARO_LOGO } from "@/lib/faro";

function formatErr(detail) {
  if (!detail) return "Error al iniciar sesión";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { login, isAuthed, checking } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (!checking && isAuthed) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenido de nuevo");
      nav("/admin");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--faro-blue-deep)] faro-noise px-5" data-testid="admin-login-page">
      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-white/70 hover:text-white mb-6">
          <ArrowLeft size={14} /> Volver a la web
        </Link>
        <div className="rounded-3xl bg-white p-8 shadow-2xl">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl overflow-hidden border border-[rgba(30,42,143,0.15)]">
              <img src={FARO_LOGO} alt="Faro" className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="font-display font-black text-lg text-[var(--faro-blue)]">Panel Faro</div>
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-[var(--faro-red)]">Zona privada</div>
            </div>
          </div>

          <h1 className="font-display font-black text-2xl mt-6 flex items-center gap-2">
            <Lock size={20} className="text-[var(--faro-blue)]" /> Iniciar sesión
          </h1>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-login-form">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)]">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email"
                className="mt-1 w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-3 outline-none focus:border-[var(--faro-blue)] focus:ring-2 focus:ring-[rgba(30,42,143,0.1)]"
                placeholder="info@autoescuelasfaro.com"
                required
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)]">Contraseña</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password"
                className="mt-1 w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-3 outline-none focus:border-[var(--faro-blue)] focus:ring-2 focus:ring-[rgba(30,42,143,0.1)]"
                placeholder="••••••••"
                required
              />
            </label>
            <button type="submit" disabled={loading} data-testid="admin-login-submit" className="btn-pill btn-secondary w-full justify-center disabled:opacity-60">
              {loading ? "Entrando..." : "Entrar"} <ArrowRight size={16} />
            </button>
            <div className="text-center pt-1">
              <Link
                to="/admin/forgot-password"
                data-testid="admin-forgot-password"
                className="text-sm font-semibold text-[var(--faro-blue)] hover:text-[var(--faro-red)] transition-colors"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
